import { getLocalTimeZone, today } from '@internationalized/date';
import { DatePicker, Icon, InlineStack, Tooltip } from '@customerio/pluma-components/react';
import {
  PLUMA_DATE_PICKER_PRESET_LAST_7_DAYS,
  PLUMA_DATE_PICKER_PRESET_LAST_30_DAYS,
  PLUMA_DATE_PICKER_PRESET_LAST_12_WEEKS_FROM_NOW,
} from '@customerio/pluma-components/react/utils/date-picker';
import { unixTimestampToCalendarDate } from '@/utils/time';
import { rangeToTimestamps } from '../utils/date-range';

/**
 * The Outbox preset set, ported from the Ember `<cio-deliveries/date-picker>`
 * (last 7 days / 30 days / 12 weeks) instead of Pluma's defaults — the defaults
 * include "Last 24 hours" and "Last 365 days", the latter exceeding the export
 * modal's 6-month `maxRange`. (Ember's cio-only "Last 6 months" preset has no
 * Pluma-core equivalent, so it's omitted.)
 */
const OUTBOX_DATE_RANGE_PRESETS = [
  PLUMA_DATE_PICKER_PRESET_LAST_7_DAYS,
  PLUMA_DATE_PICKER_PRESET_LAST_30_DAYS,
  PLUMA_DATE_PICKER_PRESET_LAST_12_WEEKS_FROM_NOW,
];

type DeliveryDateRangeProps = {
  startTs: number | null;
  endTs: number | null;
  onChange: (range: { startTs: number | null; endTs: number | null }) => void;
  isDisabled?: boolean;
  isClearable?: boolean;
  /** Max span, e.g. `'1 year'` — forwarded to the picker. */
  maxRange?: string | number;
  /** Optional help tooltip rendered beside the picker. */
  tooltipMessage?: string;
};

/**
 * The Outbox date-range filter. A thin wrapper over the Pluma range
 * `DatePicker` that maps between the deliveries query's unix-second bounds and
 * the picker's `CalendarDate` range (see `utils/date-range`). Ported from the
 * Ember `<cio-deliveries/date-picker>`.
 */
export function DeliveryDateRange({
  startTs,
  endTs,
  onChange,
  isDisabled = false,
  isClearable = true,
  maxRange = '1 year',
  tooltipMessage,
}: DeliveryDateRangeProps) {
  const todayDate = today(getLocalTimeZone());
  const value =
    startTs != null && endTs != null
      ? { start: unixTimestampToCalendarDate(startTs), end: unixTimestampToCalendarDate(endTs) }
      : undefined;

  return (
    <InlineStack alignBlock="center" gap="100">
      <DatePicker
        ariaLabel="Select range"
        placeholder="Select range…"
        value={value}
        isRange
        withPresets
        presets={OUTBOX_DATE_RANGE_PRESETS}
        maxDate={todayDate}
        maxRange={maxRange}
        size="sm"
        isDisabled={isDisabled}
        isClearable={isClearable}
        // Range mode passes a `{ start, end }` value; anything else (single/zoned
        // values this picker never produces, or null) clears the filter.
        onChange={(next) =>
          onChange(
            next != null && 'start' in next && 'end' in next ? rangeToTimestamps(next) : { startTs: null, endTs: null },
          )
        }
        onClear={() => onChange({ startTs: null, endTs: null })}
      />
      {tooltipMessage != null ? (
        <Tooltip placement="top" content={tooltipMessage}>
          <Icon name="help" />
        </Tooltip>
      ) : null}
    </InlineStack>
  );
}
